import { ConditionalAsChild } from 'src/components/primitives/ConditionalAsChild';
import { InputSize } from 'src/components/primitives/Input/types';
import { getInputSizeDependentClassName } from 'src/components/primitives/Input/utils';
import { cj, cm } from 'src/utils/classNames';
import { WithClassName, WithChildren } from 'src/utils/UtilityTypes';

interface ContainerProps extends WithClassName, WithChildren {
	disabled?: boolean;
	error?: boolean;
	/**
	 * If not passed, renders `children` within a `div`.
	 */
	asChild?: boolean;
}

function Container({
	className,
	disabled,
	error,
	children,
	asChild,
}: ContainerProps) {
	return (
		<ConditionalAsChild
			asChild={asChild}
			fallback="div"
			className={cm(
				'flex items-center rounded-sm border border-input-border bg-input-bg transition-colors',
				!disabled && !error && 'hover:border-purple-link',
				disabled && 'border-stroke-divider bg-button-disabled',
				error && 'border-negative-red',
				className
			)}
		>
			{children}
		</ConditionalAsChild>
	);
}

interface LabelProps extends WithClassName, WithChildren {
	disabled?: boolean;
	/**
	 * If not passed, renders `children` within a `div`.
	 */
	asChild?: boolean;
	size?: InputSize;
}

function Label({ className, disabled, asChild, children, size }: LabelProps) {
	const { paddingClassName } = getInputSizeDependentClassName(size);

	return (
		<ConditionalAsChild
			asChild={asChild}
			fallback="div"
			className={cm(
				'flex typo-b4 text-text-secondary transition-colors select-none',
				disabled && 'cursor-not-allowed text-text-disabled',
				paddingClassName,
				className
			)}
		>
			{children}
		</ConditionalAsChild>
	);
}

interface PrefixProps extends WithClassName, WithChildren {
	size?: InputSize;
}

function Prefix({ className, children, size }: PrefixProps) {
	const { paddingClassName } = getInputSizeDependentClassName(size);

	return (
		<div
			className={cj(
				'flex typo-b4 text-text-default select-none',
				paddingClassName,
				className
			)}
		>
			{children}
		</div>
	);
}

interface ValueProps extends WithClassName, WithChildren {
	disabled?: boolean;
	/**
	 * If not passed, renders `children` within an `div`.
	 */
	asChild?: boolean;
	size?: InputSize;
	onClick?: () => void;
}

function Value({
	size,
	asChild,
	disabled,
	className,
	children,
	onClick,
}: ValueProps) {
	const { paddingClassName, textSizeClassName } =
		getInputSizeDependentClassName(size);

	return (
		<ConditionalAsChild
			asChild={asChild}
			fallback="div"
			className={cm(
				'w-full min-w-0 flex-1 border-none bg-transparent transition-colors focus:outline-hidden',
				disabled
					? 'cursor-not-allowed text-text-disabled placeholder:text-text-disabled'
					: 'text-text-default placeholder:text-text-secondary',
				paddingClassName,
				textSizeClassName,
				className
			)}
			onClick={onClick}
		>
			{children}
		</ConditionalAsChild>
	);
}

interface SuffixProps extends WithClassName, WithChildren {
	disabled?: boolean;
	size?: InputSize;
}

function Suffix({ className, disabled, children, size }: SuffixProps) {
	const { paddingClassName, textSizeClassName } =
		getInputSizeDependentClassName(size);

	return (
		<div
			className={cm(
				'flex text-text-default select-none',
				disabled && 'cursor-not-allowed',
				paddingClassName,
				textSizeClassName,
				className
			)}
		>
			{children}
		</div>
	);
}

export default {
	Container,
	Label,
	Prefix,
	Value,
	Suffix,
};
